import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  FORM_NAME,
  STEP,
  STEPS,
  STREAM,
  RECORDED_FILE,
  TIMER_EXPIRY_TIME,
  RECORDER,
  RECORDER_STATE,
  RECORDER_STATES,
  TIMER_EXPIRY,
} from './fields';
import { Button } from 'components/button';
import { useSelector } from 'react-redux';
import { getFormValues, change } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { injectMetadata } from './webm-metadata';

export const RecordingTitle = () => {
  const { t } = useTranslation();
  const { [RECORDER_STATE]: recorderState } = useSelector(getFormValues(FORM_NAME));

  const titleText =
    recorderState === RECORDER_STATES.PAUSED || recorderState === RECORDER_STATES.WILL_RESUME
      ? t('recording_paused_title')
      : t('recording_title');

  return <>{titleText}</>;
};

export const RecordingFooter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { [RECORDER]: recorder, [RECORDER_STATE]: recorderState } = useSelector(getFormValues(FORM_NAME));

  const finishRecordingHandler = () => recorder.stop();

  return (
    <div className="d-flex justify-content-between">
      {recorderState === RECORDER_STATES.PAUSED ? (
        <Button secondary onClick={() => willResumeRecording(dispatch)}>
          {t('resume')}
        </Button>
      ) : (
        <Button secondary disabled={!recorder} onClick={() => pauseRecording(dispatch, recorder)}>
          {t('pause')}
        </Button>
      )}

      <Button disabled={!recorder} onClick={finishRecordingHandler}>
        {t('finish_recording')}
      </Button>
    </div>
  );
};

export const pauseRecording = (dispatch, recorder) => {
  recorder.pause();
  dispatch(change(FORM_NAME, RECORDER_STATE, RECORDER_STATES.PAUSED));
};

export const resumeRecording = (dispatch, recorder) => {
  recorder.resume();
  dispatch(change(FORM_NAME, RECORDER_STATE, RECORDER_STATES.RESUME));
};

export const willResumeRecording = (dispatch) => {
  dispatch(change(FORM_NAME, RECORDER_STATE, RECORDER_STATES.WILL_RESUME));
};

export const createAndStartRecorder = (stream, dispatch) => {
  async function goToNextStep(chunks) {
    const rawBlob = new Blob(chunks, { type: 'video/webm' });
    const blob = await injectMetadata(rawBlob);
    blob.lastModifiedDate = new Date();
    blob.name = `webcam_record_${new Date().getTime()}.webm`;

    dispatch(change(FORM_NAME, RECORDED_FILE, blob));
    dispatch(change(FORM_NAME, STEP, STEPS.PREVIEW));
    dispatch(change(FORM_NAME, STREAM, null));
    dispatch(change(FORM_NAME, RECORDER, null));
    dispatch(change(FORM_NAME, RECORDER_STATE, null));
  }
  const mediaRecorder = window.VideoCaptureAPI.createRecorder(stream, {
    options: { mimeType: 'video/webm' },
    stopHandler: (chunks) => goToNextStep(chunks),
  });
  mediaRecorder.start();

  stream.getVideoTracks()[0].onended = () => mediaRecorder.stop();

  const time = new Date();
  time.setSeconds(time.getSeconds() + TIMER_EXPIRY);

  dispatch(change(FORM_NAME, TIMER_EXPIRY_TIME, time));
  dispatch(change(FORM_NAME, RECORDER, mediaRecorder));
  dispatch(change(FORM_NAME, RECORDER_STATE, RECORDER_STATES.RECORDING));
};

export const useCleanUpRecorder = () => {
  const { [RECORDER]: recorder, [STREAM]: stream } = useSelector(getFormValues(FORM_NAME));

  useEffect(() => {
    if (!recorder) {
      return;
    }

    return () => {
      window.VideoCaptureAPI.stopMediaTracks(stream);
      recorder.destroy();
    };
  }, [stream, recorder]);
};
